// components/CartPopover.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Button,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ShoppingBag,
  DeleteOutline,
  Add,
  Remove,
  Close,
} from "@mui/icons-material";
import { useCart, cartLineRefForUpdate } from "../context/CartContext";
import { DEFAULT_AGENT_TAX_PERCENT } from "../utils/agentBillingPayload";

function roundMoney(n) {
  return Math.round(parseFloat(String(n ?? 0)) * 100) / 100;
}

export default function CartPopover({ anchorEl, onClose }) {
  const open = Boolean(anchorEl);
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const totalItems = cartItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );

  const formatPrice = (price) => {
    const num = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  // Calculate subtotal
  const subtotal = cartItems.reduce(
    (sum, item) => sum + (parseFloat(item.price) || 0) * item.quantity,
    0
  );
  const tax = roundMoney(subtotal * DEFAULT_AGENT_TAX_PERCENT / 100);
  const total = roundMoney(subtotal + tax);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : 360,
          p: 0,
          borderRadius: 1,
          boxShadow: "0px 25px 50px rgba(0, 0, 0, 0.25)",
          overflowY: "auto",
          maxHeight: isMobile ? "70vh" : "75vh",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <ShoppingBag fontSize="small" />
          Cart
          <Chip
            label={totalItems}
            size="small"
            color="primary"
            sx={{
              fontWeight: "bold",
              fontSize: "0.75rem",
              height: 20,
              minWidth: 24,
              textAlign: "center",
            }}
          />
        </Typography>
        <IconButton onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Empty State */}
      {cartItems.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
          <ShoppingBag sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <Typography color="text.secondary">Your cart is empty</Typography>
          <Button
            component={Link}
            to="/products"
            size="small"
            variant="outlined"
            sx={{ mt: 2 }}
          >
            Continue shopping
          </Button>
        </Box>
      ) : (
        <>
          {/* Items List */}
          <List disablePadding>
            {cartItems.map((item, idx) => (
              <React.Fragment key={item.cartLineUid ?? `${idx}-${item.id}-${item.size ?? ""}`}>
                <ListItem
                  sx={{
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={item.image_uri || item.image}
                      alt={item.name || item.title}
                      variant="rounded"
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 0.5,
                        border: "1px solid #eee",
                      }}
                    />
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{ lineHeight: 1.4 }}
                      >
                        {item.name || item.title}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        USD {formatPrice(item.price)}
                      </Typography>
                    }
                    sx={{ flex: 1 }}
                  />

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() =>
                        updateQuantity(cartLineRefForUpdate(item), Math.max(1, item.quantity - 1))
                      }
                      sx={{
                        p: 0.5,
                        border: "1px solid #ddd",
                        borderRadius: 0.5,
                        "&:hover": { bgcolor: "grey.100" },
                      }}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="body2"
                      sx={{ minWidth: 24, textAlign: "center" }}
                    >
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => updateQuantity(cartLineRefForUpdate(item), item.quantity + 1)}
                      sx={{
                        p: 0.5,
                        border: "1px solid #ddd",
                        borderRadius: 0.5,
                        "&:hover": { bgcolor: "grey.100" },
                      }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => removeFromCart(cartLineRefForUpdate(item))}
                    sx={{
                      p: 0.5,
                      "&:hover": { color: "error.main" },
                    }}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </ListItem>
                <Divider component="li" sx={{ mx: 2 }} />
              </React.Fragment>
            ))}
          </List>

          {/* Summary Section */}
          <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
            <Stack spacing={1.5}>
              {/* Subtotal */}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">
                  USD {formatPrice(subtotal)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">
                  Tax ({DEFAULT_AGENT_TAX_PERCENT}%)
                </Typography>
                <Typography variant="body2">
                  USD {formatPrice(tax)}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" pt={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Total
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold">
                  USD {formatPrice(total)}
                </Typography>
              </Box>
              <Button
                component={Link}
                to="/cart"
                variant="outlined"
                fullWidth
                sx={{
                  mt: 1,
                  py: 1.25,
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  borderRadius: 1,
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "primary.dark",
                    color: "primary.dark",
                    bgcolor: "grey.50",
                  },
                }}
                onClick={() => {
                  onClose?.();
                }}
              >
                View Cart
              </Button>

              {/* Buttons */}
              <Button
                component={Link}
                to="/checkout"
                variant="contained"
                fullWidth
                sx={{
                  mt: 1,
                  py: 1.25,
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                  borderRadius: 1,
                  bgcolor: "black",
                  color: "white",
                  "&:hover": {
                    bgcolor: "black",
                    opacity: 0.9,
                  },
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                }}
                onClick={() => {
                  onClose?.();
                }}
              >
                Proceed to Checkout
                <Box>→</Box>
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Popover>
  );
}
